import { Chart, type TooltipItem } from "chart.js";
import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { locale, t } from "../locale";
import type { Route, RouteContext } from "../router";
import { centeredBox } from "../van/centered-box";
import {
	button,
	canvas,
	div,
	h1,
	h2,
	hr,
	input,
	label,
	p,
} from "../van/van-tags";
import { sp2Placeholder } from "./sp2";

interface LoadDataPoint {
	Timestamp: string;
	Value: number;
}

class LoadChart {
	#draw(): void {
		if (!this.#dataset.length) return;

		const lowerBoundMs =
			this.#reactive.centerTimeMs -
			this.#reactive.zoomRadiusHours * 60 * 60 * 1000;

		const upperBoundMs =
			this.#reactive.centerTimeMs +
			this.#reactive.zoomRadiusHours * 60 * 60 * 1000;

		let from = 0;
		while (
			from < this.#dataset.length - 1 &&
			+new Date(this.#dataset[from].Timestamp) < lowerBoundMs
		) {
			from++;
		}

		let to = this.#dataset.length - 1;
		while (
			to > from &&
			+new Date(this.#dataset[to].Timestamp) > upperBoundMs
		) {
			to--;
		}

		if (!this.#drawing) {
			window.requestAnimationFrame(() => {
				this.#updateData(from, to);
				this.#updateOptions(from, to);
				this.#chart.update("none"); // update without animation
				this.#drawing = false;
			});
		}
		this.#drawing = true;
	}

	#onDateInput(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		this.#reactive.centerTimeMs = +e.target.value;
		this.#draw();
	}

	#onZoomInput(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		// Slider uses negative values to reverse direction
		this.#reactive.zoomRadiusHours = -1 * +e.target.value;
		this.#draw();
	}

	#updateData(from: number, to: number): void {
		const hue = getComputedStyle(document.documentElement).getPropertyValue(
			"--bulma-primary-h",
		);

		const saturation = getComputedStyle(
			document.documentElement,
		).getPropertyValue("--bulma-primary-s");

		const lightness = getComputedStyle(
			document.documentElement,
		).getPropertyValue("--bulma-primary-l");

		this.#chart.data.datasets = [
			{
				backgroundColor: `hsla(${hue}, ${saturation}, ${lightness}, 0.2)`,
				borderColor: `hsl(${hue}, ${saturation}, ${lightness})`,
				data: this.#dataset
					.slice(from, to)
					.map((datapoint) => datapoint.Value),
				label: "W",
				type: "scatter",
			},
		];

		this.#chart.data.labels = this.#dataset
			.slice(from, to)
			.map((datapoint) => datapoint.Timestamp);
	}

	#updateOptions(fromIndex: number, toIndex: number): void {
		const from = new Date(this.#dataset[fromIndex].Timestamp);
		let to = new Date(this.#dataset[toIndex].Timestamp);

		// Include the time uptil now, if the zoom interval should include it,
		// even if there are no data points. This is to clearly show that the
		// device is not communicating.
		// The -2 times zoom is because zoom is stored as a negative ms value
		// for the sake of the slider behaviour.
		const now = new Date();
		if (+from + -2 * this.#reactive.zoomRadiusHours >= +now) {
			to = now;
		}

		this.#chart.options = {
			animation: false,
			locale: "en-GB",
			maintainAspectRatio: true,
			plugins: {
				legend: {
					display: false,
					labels: {
						color: "#FFFFFF",
					},
					position: "top",
				},
				tooltip: {
					callbacks: {
						label: (tooltip: TooltipItem<"scatter">): string => {
							return `${tooltip.parsed.y} W`;
						},
						title: (tooltip: TooltipItem<"scatter">[]): string => {
							return locale.dateTimeString(tooltip[0].parsed.x);
						},
					},
					enabled: true,
					yAlign: "bottom",
				},
			},
			responsive: true,
			scales: {
				x: {
					display: true,
					type: "time",
					min: +from,
					max: +to,
					time: {
						displayFormats: {
							hour: "HH:mm",
						},
						unit:
							+to - +from <= 1000 * 60 * 60 * 48 ? "hour" : "day",
					},
				},
				y: {
					title: {
						display: true,
						text: "W",
					},
					display: true,
					min: 0,
					max: this.#ceilingWatt,
					ticks: {
						color: "#000000",
					},
				},
			},
		};
	}

	#ceilingWatt: number;
	#chart: Chart;
	#dataset: LoadDataPoint[];
	#drawing = false;
	#reactive = reactive({
		centerTimeMs: 0,
		zoomRadiusHours: 12,
	});

	constructor(container: HTMLDivElement, dataset: LoadDataPoint[]) {
		this.#ceilingWatt = Math.max(...dataset.map((point) => point.Value), 1);
		this.#ceilingWatt = Math.ceil(this.#ceilingWatt * 1.2);

		const maxDateTime = new Date();
		maxDateTime.setHours(12, 0, 0, 0);

		const minDateTime = new Date(dataset[0].Timestamp);
		minDateTime.setHours(12, 0, 0, 0);

		this.#reactive.centerTimeMs = +maxDateTime;

		const chartCanvas = canvas();
		container.replaceChildren(
			chartCanvas,
			p(
				{ class: "title is-6" },
				() =>
					`${locale.dateString(this.#reactive.centerTimeMs)} ± ${this.#reactive.zoomRadiusHours} hours`,
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Time"),
				div(
					{ class: "control" },
					input({
						class: "slider is-fullwidth",
						max: +maxDateTime,
						min: +minDateTime,
						oninput: (e: Event) => this.#onDateInput(e),
						step: 1000 * 60 * 60 * 24, // one day in ms
						type: "range",
						value: +maxDateTime,
					}),
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Zoom"),
				div(
					{ class: "control" },
					input({
						// slider values are negative to reverse direction
						class: "slider is-fullwidth",
						max: -12, // hours
						min: -24 * 7, // one week in hours
						oninput: (e: Event) => this.#onZoomInput(e),
						step: 6, // hours
						type: "range",
						value: -12, // default to narrowest zoom
					}),
				),
			),
		);

		this.#chart = new Chart(chartCanvas, {
			data: {
				datasets: [],
			},
			type: "scatter",
		});

		this.#dataset = dataset;
		this.#draw();
	}
}

export class Sp2LogsPage implements Route {
	async #getLoad(imei: string): Promise<LoadDataPoint[]> {
		const req = this.#ctx.api.get(`/smartplug2s/${imei}/load`);
		req.setSignedInUserAuth();

		const res = await req.do();
		if (res.ok) {
			return await res.json();
		} else {
			return [];
		}
	}

	async #getSp2(imei: string): Promise<void> {
		const req = this.#ctx.api.get(`/smartplug2s/${imei}`);
		req.setSignedInUserAuth();

		const res = await req.do();
		if (res.ok) {
			this.#reactive.sp2 = await res.json();
		}
	}

	async refresh(match: Match): Promise<void> {
		if (!match?.data?.imei) throw "missing path param: imei";
		const imei = match.data.imei;
		await this.#getSp2(imei);

		new LoadChart(this.#loadChartContainer, await this.#getLoad(imei));
	}

	#ctx: RouteContext;
	#loadChartContainer = div();
	#reactive = reactive({
		sp2: sp2Placeholder,
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "wide" },
			h1({ class: "title is-4" }, () => this.#reactive.sp2.Metadata.Name),
			p({ class: "subtitle is-6" }, "SmartPlug2"),
			hr(),
			// h2({ class: "title is-5" }, t("sp2LogsPage.consumptionHeader")),
			// hr(),
			h2({ class: "title is-5" }, t("sp2LogsPage.loadHeader")),
			this.#loadChartContainer,
			hr(),
			div(
				{ class: "field" },
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () =>
								this.#ctx.router.jump(
									`/devices?id=${this.#reactive.sp2.Imei}`,
								),
						},
						t("sp2LogsPage.backButton"),
					),
				),
			),
		);
	}
}
